import { getPromoV2 } from 'api/promo'
import { CHECKOUT_SET_EXPERIENCE_UPSELL_BENEFIT, CHECKOUT_EXPERIENCE_UPSELL_REQUEST, CHECKOUT_EXPERIENCE_UPSELL_APPLIED } from 'actions/actionConstants'
import type { AppAction, DefinedAppAction } from 'actions/types/ActionTypes'
import type { ThunkAction } from '@reduxjs/toolkit'
import buildExperienceUpsellDiscountOrder from 'checkout/lib/experiences/buildExperienceUpsellDiscountOrder'
import { getExperienceUpsellPricingKey, hasExperienceUpsellComposition } from 'checkout/selectors/view/experienceUpsellPricing'
import uuidV4 from 'lib/string/uuidV4Utils'
import { isSpoofed } from 'selectors/isSpoofed'

export function registerExperienceUpsellCampaign(codeName: string, kind: 'upsell' | 'inflow-bundle' = 'upsell'): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    if (state.checkout.cart.experienceUpsellBenefit?.codeName === codeName) return
    const alternative = { promoCode: state.checkout.payment.promoCode, voucherIds: state.checkout.payment.voucherIds ?? [] }
    dispatch({
      type: CHECKOUT_SET_EXPERIENCE_UPSELL_BENEFIT,
      benefit: { codeName, ...(kind === 'inflow-bundle' ? { kind } : {}), choice: alternative.promoCode || alternative.voucherIds.length ? 'existing' : 'upsell', alternative },
    })
  }
}

export function applyExperienceUpsellVoucherSelection(voucherIds: Array<string>): ThunkAction<Promise<void>, App.State, undefined, DefinedAppAction> {
  return async(dispatch, getState) => {
    const benefit = getState().checkout.cart.experienceUpsellBenefit
    if (!benefit) return
    dispatch({
      type: CHECKOUT_SET_EXPERIENCE_UPSELL_BENEFIT,
      benefit: { ...benefit, alternative: { ...benefit.alternative, voucherIds } },
    })
    await dispatch(applyExperienceUpsellBenefit('existing', true))
  }
}

export function applyExperienceUpsellBenefit(choice: 'upsell' | 'existing', force = false): ThunkAction<Promise<void>, App.State, undefined, DefinedAppAction> {
  return async(dispatch, getState) => {
    const state = getState()
    const benefit = state.checkout.cart.experienceUpsellBenefit
    const memberId = state.auth.account.memberId
    if (!benefit || !memberId) return
    const key = getExperienceUpsellPricingKey(state)
    const request = state.checkout.experienceUpsellRequest
    if (!force && request?.key === key && request.choice === choice && request.status !== 'error') return
    const id = uuidV4()
    dispatch({ type: CHECKOUT_EXPERIENCE_UPSELL_REQUEST, request: { id, key, choice, status: 'loading' } })
    const stillCurrent = () => getState().checkout.experienceUpsellRequest?.id === id && getExperienceUpsellPricingKey(getState()) === key
    try {
      const eligible = hasExperienceUpsellComposition(state)
      const promoCode = choice === 'upsell' ? (eligible ? benefit.codeName : undefined) : benefit.alternative.promoCode
      const voucherIds = choice === 'upsell' ? [] : benefit.alternative.voucherIds
      const order = buildExperienceUpsellDiscountOrder(state)
      if (!order.items.length && eligible) {
        if (stillCurrent()) dispatch({ type: CHECKOUT_EXPERIENCE_UPSELL_REQUEST, request: { id, key, choice, status: 'error' } })
        return
      }
      const promotion = promoCode || voucherIds.length ? await getPromoV2({
        codeName: promoCode,
        voucherIds,
        order,
        userId: memberId,
        requestSource: isSpoofed(state) ? 'global-checkout-spoof' : 'global-checkout',
      }) : undefined
      if (!stillCurrent()) return
      dispatch({
        type: CHECKOUT_EXPERIENCE_UPSELL_APPLIED,
        id,
        choice,
        voucherIds,
        promotion: promotion ? { ...promotion, hidePromoCode: choice === 'upsell', experienceUpsellPricingKey: key } : undefined,
      })
    } catch {
      if (stillCurrent()) dispatch({ type: CHECKOUT_EXPERIENCE_UPSELL_REQUEST, request: { id, key, choice, status: 'error' } })
    }
  }
}
